import { useEffect } from 'react';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import SocialProofSection from './components/SocialProofSection';
import AdvantagesSection from './components/AdvantagesSection';
import ProcessSection from './components/ProcessSection';
import PortfolioSection from './components/PortfolioSection';
import PricingSection from './components/PricingSection';
import FAQSection from './components/FAQSection';
import FinalCTASection from './components/FinalCTASection';
import FooterSection from './components/FooterSection';
import FloatingCTA from './components/FloatingCTA';

export default function HomePage() {
  useEffect(() => {
    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    if (canonical) {
      canonical.href = `${siteUrl}/`;
    } else {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = `${siteUrl}/`;
      document.head.appendChild(canonical);
    }

    // Organization JSON-LD
    const orgScript = document.createElement('script');
    orgScript.type = 'application/ld+json';
    orgScript.id = 'ld-organization';
    orgScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ProfessionalService',
      name: 'UzunDigital',
      url: siteUrl,
      logo: 'https://static.readdy.ai/image/a17d3ae5e640a18a289681692364ae47/fc2ed0bd71fb688a40f03f8096cc646d.jpeg',
      image: 'https://static.readdy.ai/image/a17d3ae5e640a18a289681692364ae47/fc2ed0bd71fb688a40f03f8096cc646d.jpeg',
      description: 'UzunDigital crée des sites internet pros personnalisés. Site vitrine ou e-commerce à partir de 69€/mois sans engagement.',
      email: 'uzundigital71@gmail.com',
      telephone: '+33651872435',
      areaServed: 'FR',
      priceRange: '€€',
      knowsLanguage: 'fr',
      sameAs: [],
    });

    // WebSite JSON-LD
    const siteScript = document.createElement('script');
    siteScript.type = 'application/ld+json';
    siteScript.id = 'ld-website';
    siteScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'UzunDigital',
      url: siteUrl,
      description: 'Création de sites internet professionnels à partir de 400€ – livrés en 7 jours maximum.',
      inLanguage: 'fr-FR',
      potentialAction: {
        '@type': 'ReadAction',
        target: [`${siteUrl}/offres`, `${siteUrl}/demande-devis`],
      },
    });

    // FAQPage JSON-LD
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.id = 'ld-faq';
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Que comprend exactement le site à 400€ ?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Vous obtenez un site vitrine complet et personnalisé : design sur-mesure, responsive mobile, formulaire de contact ou de devis, optimisation SEO de base, et livraison en 7 jours maximum (48h en moyenne). La maintenance mensuelle à 50€/mois est incluse pour garantir la sécurité et le bon fonctionnement de votre site.',
          },
        },
        {
          '@type': 'Question',
          name: 'Comment modifier mon site après livraison ?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Toutes les modifications de votre site passent par notre équipe. Il vous suffit de nous contacter par email ou téléphone avec votre demande, et nous nous en occupons rapidement.',
          },
        },
        {
          '@type': 'Question',
          name: "Que se passe-t-il si je ne suis pas satisfait ?",
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Votre satisfaction est notre priorité. Nous travaillons avec vous jusqu'à ce que le résultat vous convienne. Nous effectuons des ajustements gratuits avant la livraison finale.",
          },
        },
        {
          '@type': 'Question',
          name: 'Comment se passe le paiement ?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Le paiement se fait à la livraison, une fois que vous êtes satisfait du résultat. Nous proposons également le paiement en plusieurs fois pour plus de flexibilité.",
          },
        },
      ],
    });

    // WebPage JSON-LD
    const pageScript = document.createElement('script');
    pageScript.type = 'application/ld+json';
    pageScript.id = 'ld-webpage-home';
    pageScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'UzunDigital – Création site Clé en Main',
      url: `${siteUrl}/`,
      description: 'UzunDigital crée des sites internet pros personnalisés. Site vitrine ou e-commerce à partir de 69€/mois sans engagement',
      inLanguage: 'fr-FR',
      isPartOf: { '@type': 'WebSite', url: siteUrl },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Accueil', item: `${siteUrl}/` },
        ],
      },
    });

    document.head.appendChild(orgScript);
    document.head.appendChild(siteScript);
    document.head.appendChild(faqScript);
    document.head.appendChild(pageScript);

    return () => {
      ['ld-organization', 'ld-website', 'ld-faq', 'ld-webpage-home'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <Navbar />

      {/* 1️⃣ HERO */}
      <HeroSection />

      {/* 2️⃣ PREUVES SOCIALES */}
      <SocialProofSection />

      {/* 3️⃣ AVANTAGES */}
      <div id="avantages">
        <AdvantagesSection />
      </div>

      {/* 4️⃣ PROCESS EN 3 ÉTAPES */}
      <ProcessSection />

      {/* 5️⃣ PORTFOLIO */}
      <div id="portfolio">
        <PortfolioSection />
      </div>

      {/* 6️⃣ TARIFS */}
      <div id="tarifs">
        <PricingSection />
      </div>

      {/* 7️⃣ FAQ */}
      <div id="faq">
        <FAQSection />
      </div>

      {/* 8️⃣ CTA FINAL */}
      <FinalCTASection />

      {/* FOOTER */}
      <FooterSection />

      {/* BARRE CTA FLOTTANTE */}
      <FloatingCTA />
    </div>
  );
}
